// NEW_FILE_JUSTIFIED: New hook for Telegram personal messages feature — composing queries/mutations/actions from the telegram/personal Convex module. No existing hook covers this domain.
/**
 * useTelegramMessages Hook
 * Composes Convex queries, mutations, and actions for the Telegram personal
 * messages feature. Maps raw Convex data to the IConversation / IMessage
 * shapes expected by ConversationList and ChatPanel.
 */

import { useQuery, useMutation, useAction } from 'convex/react';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: Convex generated API lives outside src/; no @convex/ path alias in tsconfig — all hooks in src/hooks/ use this relative path
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: Convex generated types
import type {
  IConversation,
  IMessage,
  IUseTelegramMessagesReturn,
  ISendMediaArgs,
} from '@/@types/messages';
import { getInitials, getAvatarColor, formatTime } from '@/lib/message-utils';

// ============================================================================
// Hook
// ============================================================================

export function useTelegramMessages(selectedContactId: string | null): IUseTelegramMessagesReturn {
  // Live conversation list — updates in real time via Convex subscription
  const rawConversations = useQuery(api.telegram.personal.queries.getConversations, {});

  // Live messages for the selected contact — skipped when nothing is selected
  const rawMessages = useQuery(
    api.telegram.personal.queries.getMessages,
    selectedContactId !== null ? { telegramContactId: selectedContactId } : 'skip'
  );

  const markReadMutation = useMutation(api.telegram.personal.mutations.markRead);
  const sendMessageAction = useAction(api.telegram.personal.actions.sendMessage);
  const sendMediaMessageAction = useAction(api.telegram.personal.actions.sendMediaMessage);
  const sendReactionAction = useAction(api.telegram.personal.actions.sendReaction);
  const deleteMessageAction = useAction(api.telegram.personal.actions.deleteMessageAction);

  // Contact metadata (phone, bio) — reactive subscription to telegramContacts table
  const rawContact = useQuery(
    api.telegram.personal.queries.getContact,
    selectedContactId !== null ? { telegramContactId: selectedContactId } : 'skip'
  );

  const fetchContactBioAction = useAction(api.telegram.personal.actions.fetchContactBio);

  // Optimistic pending reactions — shown immediately while server round-trip completes
  const [pendingReactions, setPendingReactions] = useState<
    Map<number, { emoji: string; pending: true }>
  >(new Map());

  // Clear pending reactions when real data updates (Convex subscription refreshed)
  useEffect(() => {
    if (rawMessages !== undefined && pendingReactions.size > 0) {
      setPendingReactions(prev => {
        const next = new Map(prev);
        for (const msgId of prev.keys()) {
          // If the server data now reflects a change for this message, remove pending
          const serverMsg = rawMessages.find(m => m.telegramMessageId === msgId);
          if (serverMsg) {
            next.delete(msgId);
          }
        }
        return next.size === prev.size ? prev : next;
      });
    }
  }, [rawMessages, pendingReactions.size]);

  // Map Convex conversation rows to IConversation for ConversationList
  const conversations = useMemo((): IConversation[] => {
    if (rawConversations === undefined) return [];
    return rawConversations.map(conv => {
      const isGroup = conv.chatType === 'group';
      const lastMessage =
        isGroup && conv.lastMessageSenderName
          ? `${conv.lastMessageSenderName}: ${conv.lastMessage}`
          : conv.lastMessage;

      return {
        id: conv.telegramContactId,
        contactName: conv.contactName,
        contactInitials: getInitials(conv.contactName),
        contactUsername: conv.contactUsername,
        avatarColor: getAvatarColor(conv.telegramContactId),
        lastMessage,
        lastMessageTime: formatTime(conv.lastMessageTimestamp),
        unreadCount: conv.unreadCount,
        isStarred: false,
        channel: 'telegram',
        chatType: conv.chatType,
        lastMessageSenderName: conv.lastMessageSenderName,
        profilePicUrl: conv.profilePicUrl,
      };
    });
  }, [rawConversations]);

  // Map Convex message rows to IMessage for ChatPanel / MessageBubble.
  // Note: getMessages does not return telegramContactId — we use selectedContactId
  // from the hook argument (query is skipped when selectedContactId is null).
  const messages = useMemo((): IMessage[] => {
    if (rawMessages === undefined) return [];
    const contactId = selectedContactId ?? '';
    return rawMessages.map(msg => {
      // Merge pending optimistic reaction if one exists for this message
      let reactions = msg.reactions;
      const pending = pendingReactions.get(msg.telegramMessageId);
      if (pending) {
        const existing = reactions ?? [];
        const alreadyHas = existing.some(r => r.emoji === pending.emoji);
        reactions = alreadyHas ? existing : [...existing, { emoji: pending.emoji }];
      }

      return {
        id: msg._id,
        conversationId: contactId,
        sender: msg.direction === 'incoming' ? 'lead' : 'agent',
        senderName: msg.direction === 'incoming' ? msg.contactName : 'You',
        senderInitials: msg.direction === 'incoming' ? getInitials(msg.contactName) : 'ME',
        avatarColor: msg.direction === 'incoming' ? getAvatarColor(contactId) : '#B8EF70',
        text:
          msg.mediaType &&
          /^\[(?:Image|Photo|Video|Audio|Document|Sticker|Voice message|File(?::\s.*)?)\]$/.test(
            msg.text ?? ''
          )
            ? ''
            : (msg.text ?? ''),
        timestamp: formatTime(msg.timestamp),
        isOutgoing: msg.direction === 'outgoing',
        groupSenderName: msg.chatType === 'group' ? msg.senderName : undefined,
        imageThumbnail: msg.mediaThumbnail,
        mediaStorageId: msg.mediaStorageId,
        mediaType: msg.mediaType,
        mediaUrl: msg.mediaUrl,
        mediaFilename: msg.mediaFilename,
        mediaMimeType: msg.mediaMimeType,
        mediaSize: msg.mediaSize,
        linkPreviewUrl: msg.linkPreviewUrl,
        linkPreviewTitle: msg.linkPreviewTitle,
        linkPreviewDescription: msg.linkPreviewDescription,
        linkPreviewSiteName: msg.linkPreviewSiteName,
        linkPreviewImageThumbnail: msg.linkPreviewImageThumbnail,
        reactions,
        replyToMessageId: msg.replyToMessageId,
        replyToText: msg.replyToText,
        replyToSenderName: msg.replyToSenderName,
        deletedForMe: msg.deletedForMe,
        deletedForEveryone: msg.deletedForEveryone,
        platformMessageId: msg.telegramMessageId,
        isFromMe: msg.direction === 'outgoing',
      };
    });
  }, [rawMessages, selectedContactId, pendingReactions]);

  const markRead = useCallback(
    async (telegramContactId: string): Promise<void> => {
      await markReadMutation({ telegramContactId });
    },
    [markReadMutation]
  );

  const sendMessage = useCallback(
    async (args: {
      telegramContactId: string;
      contactName: string;
      contactUsername?: string;
      text: string;
      chatType?: 'personal' | 'group';
      replyToMessageId?: number;
    }): Promise<void> => {
      await sendMessageAction(args);
    },
    [sendMessageAction]
  );

  const sendMediaMessage = useCallback(
    async (args: ISendMediaArgs): Promise<void> => {
      await sendMediaMessageAction({
        telegramContactId: args.contactId,
        contactName: args.contactName,
        contactUsername: args.contactUsername,
        storageId: args.storageId as Id<'_storage'>, // AS_JUSTIFIED: storageId is generated by Convex storage upload, guaranteed to be valid
        mediaType: args.mediaType,
        filename: args.filename,
        mimeType: args.mimeType,
        size: args.size,
        text: args.text,
        chatType: args.chatType,
      });
    },
    [sendMediaMessageAction]
  );

  const sendReaction = useCallback(
    async (telegramMessageId: number, emoji: string): Promise<void> => {
      if (selectedContactId === null) return;

      // Show optimistic reaction immediately
      setPendingReactions(prev => {
        const next = new Map(prev);
        next.set(telegramMessageId, { emoji, pending: true });
        return next;
      });

      try {
        await sendReactionAction({
          telegramContactId: selectedContactId,
          telegramMessageId,
          emoji,
        });
      } finally {
        // Clear pending after server responds (success or error)
        setPendingReactions(prev => {
          const next = new Map(prev);
          next.delete(telegramMessageId);
          return next;
        });
      }
    },
    [sendReactionAction, selectedContactId]
  );

  const deleteMessage = useCallback(
    async (telegramMessageId: number, forEveryone: boolean): Promise<void> => {
      if (selectedContactId === null) return;
      await deleteMessageAction({
        telegramContactId: selectedContactId,
        telegramMessageId,
        forEveryone,
      });
    },
    [deleteMessageAction, selectedContactId]
  );

  const contactDetails = useMemo(() => {
    if (rawContact === undefined || rawContact === null) return null;
    return {
      phone: rawContact.contactPhone,
      bio: rawContact.bio,
      bioFetchedAt: rawContact.bioFetchedAt,
      profilePicUrl: rawContact.profilePicUrl,
    };
  }, [rawContact]);

  const fetchBio = useCallback(
    async (telegramContactId: string): Promise<void> => {
      await fetchContactBioAction({ telegramContactId });
    },
    [fetchContactBioAction]
  );

  return {
    conversations,
    messages,
    isLoading: rawConversations === undefined,
    markRead,
    sendMessage,
    sendMediaMessage,
    contactDetails,
    fetchBio,
    sendReaction,
    deleteMessage,
  };
}
